import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Autocomplete,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import { ProgramVersionItemDto } from '../../../api/types/dtos/program';
import { useCreateProgramVersionItem, useUpdateProgramVersionItem } from '../../../api/hooks/useProgramVersionItems';
import { useProgramLevels } from '../../../api/hooks/useProgramLevels';
import { useMeasurementTypes } from '../../../api/hooks/useMeasurementTypes';
import { useOrphanForms } from '../../../api/hooks/useForms';
import { FormSummaryDto, formService, formDtoToForm, formToCreateFormRequest, formToUpdateFormDto } from '../../../api/services/form.service';
import { useOrganization } from '../../../contexts/OrganizationContext';
import FormCreator from '../../../components/forms/FormCreator';
import { Form } from '../../../api/types/forms';

const JSON_MEASUREMENT_TYPE_ID = '19c81465-7b4d-40f7-a410-47d1819196dd';

interface CreateVersionItemModalProps {
  open: boolean;
  onClose: () => void;
  programId: string;
  versionId: string;
  existingItems?: ProgramVersionItemDto[];
  editingItem?: ProgramVersionItemDto | null;
}

const CreateVersionItemModal: React.FC<CreateVersionItemModalProps> = ({
  open,
  onClose,
  programId,
  versionId,

  editingItem = null,
}) => {
  const [id, setId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [programLevelId, setProgramLevelId] = useState('');
  const [measurementTypeId, setMeasurementTypeId] = useState('');
  const [cpNumber, setCpNumber] = useState('');

  // Form-related state
  const [formId, setFormId] = useState<string | undefined>(undefined);
  const [measurementTemplate, setMeasurementTemplate] = useState<Form | null>(null);
  const [showFormCreator, setShowFormCreator] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [isSavingForm, setIsSavingForm] = useState(false);

  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateProgramVersionItem(
    selectedOrganization?.id || '',
    programId,
    versionId
  );
  const updateMutation = useUpdateProgramVersionItem(
    selectedOrganization?.id || '',
    programId,
    versionId
  );

  const { data: programLevels = [] } = useProgramLevels(
    selectedOrganization?.id || '',
    programId,
    { isActive: true }
  );

  const { data: measurementTypes = [] } = useMeasurementTypes({ isActive: true });

  const isJsonMeasurementType = measurementTypeId === JSON_MEASUREMENT_TYPE_ID;

  const { data: orphanForms = [], isLoading: isLoadingOrphans } = useOrphanForms(
    selectedOrganization?.id || ''
  );

  // The currently-edited item's form is linked in the DB, so it is not orphan;
  // merge it client-side so the user can re-pick it after unlinking without saving first.
  const orphanFormOptions = useMemo<FormSummaryDto[]>(() => {
    if (!editingItem?.formId || !measurementTemplate) return orphanForms;
    const alreadyListed = orphanForms.some((f) => f.id === editingItem.formId);
    if (alreadyListed) return orphanForms;
    return [
      ...orphanForms,
      {
        id: editingItem.formId,
        stringId: measurementTemplate.stringId || undefined,
        title: measurementTemplate.title || 'Sin título',
        description: measurementTemplate.description || undefined,
        isActive: true,
        questionCount: measurementTemplate.questions?.length || 0,
        updatedAt: new Date().toISOString(),
      },
    ];
  }, [orphanForms, editingItem?.formId, measurementTemplate]);

  // Single consolidated useEffect for loading item data
  useEffect(() => {
    if (!open) return;

    const loadItemData = async () => {
      if (editingItem) {
        // Editing existing item
        setId(editingItem.id || null);
        setName(editingItem.name);
        setDescription(editingItem.description || '');
        setProgramLevelId(editingItem.programLevelId);
        setMeasurementTypeId(editingItem.measurementTypeId);
        setCpNumber(editingItem.cpNumber);
        setShowFormCreator(false);

        // Load form from backend if formId exists
        if (editingItem.formId && selectedOrganization?.id) {
          setIsLoadingForm(true);
          try {
            const response = await formService.getById(selectedOrganization.id, editingItem.formId);
            const dto = (response as any)?.data;
            if (dto) {
              const form = formDtoToForm(dto);
              setMeasurementTemplate(form);
              setFormId(dto.id || editingItem.formId);
            } else {
              setFormId(editingItem.formId);
              setMeasurementTemplate(null);
            }
          } catch (error) {
            console.error('Error loading form:', error);
            // Keep the formId even if form load fails (for manual fix)
            setFormId(editingItem.formId);
            setMeasurementTemplate(null);
            enqueueSnackbar('Error al cargar el formulario del item.', { variant: 'warning' });
          } finally {
            setIsLoadingForm(false);
          }
        } else if (!editingItem.formId && editingItem.measurementTemplate) {
          // Handle legacy measurementTemplate (for items not yet migrated)
          setFormId(undefined);
          if (typeof editingItem.measurementTemplate === 'string') {
            try {
              const parsed = JSON.parse(editingItem.measurementTemplate) as Form;
              setMeasurementTemplate(parsed);
            } catch {
              setMeasurementTemplate(null);
            }
          } else {
            setMeasurementTemplate(editingItem.measurementTemplate);
          }
        } else {
          // No formId and no legacy template
          setFormId(undefined);
          setMeasurementTemplate(null);
        }
      } else {
        // Creating new item
        setId(uuidv4());
        setName('');
        setDescription('');
        setProgramLevelId('');
        setMeasurementTypeId('');
        setCpNumber('');
        setFormId(undefined);
        setMeasurementTemplate(null);
        setShowFormCreator(false);
      }
    };

    loadItemData();
  }, [open, editingItem, selectedOrganization?.id, enqueueSnackbar]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (!programLevelId) {
      enqueueSnackbar('El nivel del programa es requerido', { variant: 'error' });
      return;
    }

    if (!measurementTypeId) {
      enqueueSnackbar('El tipo de medición es requerido', { variant: 'error' });
      return;
    }

    if (!cpNumber.trim()) {
      enqueueSnackbar('El número CP es requerido', { variant: 'error' });
      return;
    }

    // Only require template if measurement type is JSON
    if (isJsonMeasurementType && !formId && !measurementTemplate) {
      enqueueSnackbar('La plantilla de medición es requerida para el tipo JSON', { variant: 'error' });
      return;
    }

    try {
      // For the JSON measurement type the form reference lives in formId; the
      // empty-string sentinel plus a null formId tells the backend to CLEAR the link.
      // Legacy items without formId keep their inline template so it is not wiped
      // on save before the item is migrated to a linked form.
      const templateToSend = isJsonMeasurementType
        ? (formId ? '' : (measurementTemplate ? JSON.stringify(measurementTemplate) : ''))
        : (measurementTemplate ? JSON.stringify(measurementTemplate) : undefined);

      const itemData: Partial<ProgramVersionItemDto> = {
        ...(id && !editingItem?.id && { id }),
        name: name.trim(),
        description: description.trim() || undefined,
        versionId,
        programLevelId,
        measurementTypeId,
        cpNumber: cpNumber.trim(),
        formId: isJsonMeasurementType ? formId : undefined,
        measurementTemplate: templateToSend,
        isActive: true,
      };

      if (editingItem?.id) {
        await updateMutation.mutateAsync({
          id: editingItem.id,
          item: itemData as ProgramVersionItemDto,
        });
        enqueueSnackbar('Item actualizado exitosamente', { variant: 'success' });
      } else {
        await createMutation.mutateAsync(itemData as ProgramVersionItemDto);
        enqueueSnackbar('Item creado exitosamente', { variant: 'success' });
      }

      handleClose();
    } catch (error) {
      enqueueSnackbar(`Error al ${editingItem ? 'actualizar' : 'crear'} el item`, { variant: 'error' });
      console.error(`Error ${editingItem ? 'updating' : 'creating'} item:`, error);
    }
  };

  const handleClose = () => {
    setId(null);
    setName('');
    setDescription('');
    setProgramLevelId('');
    setMeasurementTypeId('');
    setCpNumber('');
    setFormId(undefined);
    setMeasurementTemplate(null);
    setShowFormCreator(false);
    onClose();
  };

  const handleFormSave = async (form: Form) => {
    if (!selectedOrganization?.id) return;

    setIsSavingForm(true);
    try {
      if (formId) {
        // Update existing form
        const updateRequest = formToUpdateFormDto(form);
        const response = await formService.update(selectedOrganization.id, formId, updateRequest);
        const dto = (response as any)?.data;
        if (dto) {
          const updatedForm = formDtoToForm(dto);
          setMeasurementTemplate(updatedForm);
          setFormId(dto.id || formId);
        }
        enqueueSnackbar('Formulario actualizado exitosamente', { variant: 'success' });
      } else {
        // Create new form
        const createRequest = formToCreateFormRequest(form, selectedOrganization.id);
        const response = await formService.create(selectedOrganization.id, createRequest);
        const dto = (response as any)?.data;
        if (dto?.id) {
          const newForm = formDtoToForm(dto);
          setMeasurementTemplate(newForm);
          setFormId(dto.id);
          enqueueSnackbar('Formulario creado exitosamente', { variant: 'success' });
        } else {
          enqueueSnackbar('El formulario fue creado pero no se pudo vincular', { variant: 'warning' });
        }
      }
    } catch (error) {
      console.error('Error saving form:', error);
      enqueueSnackbar('Error al guardar el formulario', { variant: 'error' });
    } finally {
      setIsSavingForm(false);
    }

    setShowFormCreator(false);
  };

  const handleLinkOrphanForm = async (summary: FormSummaryDto) => {
    if (!selectedOrganization?.id || !summary?.id) return;

    setIsLoadingForm(true);
    try {
      const response = await formService.getById(selectedOrganization.id, summary.id);
      const dto = (response as any)?.data;
      if (dto) {
        setMeasurementTemplate(formDtoToForm(dto));
        setFormId(dto.id || summary.id);
      } else {
        setFormId(summary.id);
        setMeasurementTemplate(null);
      }
    } catch (error) {
      console.error('Error loading form:', error);
      enqueueSnackbar('No se pudo cargar el formulario seleccionado', { variant: 'error' });
    } finally {
      setIsLoadingForm(false);
    }
  };

  const handleUnlinkForm = () => {
    setFormId(undefined);
    setMeasurementTemplate(null);
    enqueueSnackbar('Formulario desvinculado', { variant: 'info' });
  };

  const isEditing = !!editingItem;
  const hasForm = !!(formId || measurementTemplate);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={showFormCreator ? "lg" : "md"}
      fullWidth
      fullScreen={showFormCreator}
    >
      <DialogTitle>
        {isEditing ? 'Editar Item de Versión' : 'Crear Nuevo Item de Versión'}
      </DialogTitle>
      <DialogContent>
        {showFormCreator ? (
          <Box sx={{ mt: 2 }}>
            <FormCreator
              initialForm={measurementTemplate}
              onSave={handleFormSave}
              onCancel={() => setShowFormCreator(false)}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Número CP"
              value={cpNumber}
              onChange={(e) => setCpNumber(e.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Nivel del Programa</InputLabel>
              <Select
                value={programLevelId}
                onChange={(e) => setProgramLevelId(e.target.value)}
                label="Nivel del Programa"
              >
                {programLevels.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Tipo de Medición</InputLabel>
              <Select
                value={measurementTypeId}
                onChange={(e) => {
                  setMeasurementTypeId(e.target.value);
                  // Clear template if switching away from JSON type
                  if (e.target.value !== JSON_MEASUREMENT_TYPE_ID) {
                    setMeasurementTemplate(null);
                    setFormId(undefined);
                  }
                }}
                label="Tipo de Medición"
                renderValue={(selected) => {
                  const selectedType = measurementTypes.find(t => t.id === selected);
                  if (!selectedType) return '';
                  return `${selectedType.name}${selectedType.description ? ` - ${selectedType.description}` : ''}`;
                }}
              >
                {measurementTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    <Box>
                      <Typography variant="body1">{type.name}</Typography>
                      {type.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {type.description}
                        </Typography>
                      )}
                      {type.unit && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Unidad: {type.unit}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isJsonMeasurementType && (
              <Box sx={{ mt: 2 }}>
                {isLoadingForm ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : hasForm ? (
                  <>
                    <Alert
                      severity="success"
                      sx={{ mb: 2 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleUnlinkForm}
                          disabled={isSavingForm}
                        >
                          Desvincular
                        </Button>
                      }
                    >
                      Formulario vinculado: {measurementTemplate?.title || 'Sin título'}
                      {formId ? ` (UUID: ${formId.substring(0, 8)}...)` : ''}
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setShowFormCreator(true)}
                        disabled={isSavingForm}
                        startIcon={isSavingForm && <CircularProgress size={20} />}
                      >
                        Editar Formulario
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Debe vincular o crear un formulario para este item (tipo JSON)
                    </Alert>
                    <Autocomplete
                      options={orphanFormOptions}
                      loading={isLoadingOrphans}
                      getOptionLabel={(option) => option.title}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          void handleLinkOrphanForm(newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vincular formulario existente"
                          placeholder="Busque un formulario sin vincular..."
                          size="small"
                          helperText="Solo se listan formularios que no están vinculados a ningún item"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingOrphans ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key: _key, ...rest } = props;
                        return (
                          <Box component="li" key={option.id} {...rest}>
                            <Box>
                              <Typography variant="body1">{option.title}</Typography>
                              {option.stringId && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {option.stringId}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" display="block">
                                {option.questionCount} pregunta(s)
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => setShowFormCreator(true)}
                        disabled={isSavingForm}
                        startIcon={isSavingForm && <CircularProgress size={20} />}
                      >
                        Crear Formulario
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      {!showFormCreator && (
        <DialogActions>
          <Button onClick={handleClose} disabled={createMutation.isPending || updateMutation.isPending || isLoadingForm}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              createMutation.isPending ||
              updateMutation.isPending ||
              isLoadingForm ||
              (isJsonMeasurementType && !formId && !measurementTemplate)
            }
            startIcon={(createMutation.isPending || updateMutation.isPending) && <CircularProgress size={20} />}
          >
            {isEditing ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateVersionItemModal;
